import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, shareReplay, tap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class TranslationExtenderService {
  private http = inject(HttpClient);
  private translate = inject(TranslateService);

  private readonly remainingModules = [
    'sidebar', 'security', 'kyc', 'support',
    'dashboard', 'accounts', 'crypto', 'settings', 'cards', 'transactions', 'modals'
  ];

  // Tracks which modules have been loaded per language
  private loaded = new Map<string, Set<string>>();
  // Stores the in-flight Observable per language so concurrent callers share one set of HTTP requests
  private inFlight = new Map<string, Observable<any>>();

  load(lang: string): Observable<any> {
    // Already fully loaded — return immediately
    const alreadyLoaded = this.loaded.get(lang) ?? new Set<string>();
    const toLoad = this.remainingModules.filter(m => !alreadyLoaded.has(m));
    if (toLoad.length === 0) {
      return of(null);
    }

    // Already loading — reuse the same in-flight Observable (no duplicate HTTP requests)
    if (this.inFlight.has(lang)) {
      return this.inFlight.get(lang)!;
    }

    const requests = toLoad.map(section =>
      this.http.get<Record<string, any>>(`./assets/i18n/${lang}/${section}.json`).pipe(
        catchError(() => of({}))
      )
    );

    const load$ = forkJoin(requests).pipe(
      tap(translations => {
        const merged = translations.reduce(
          (acc: Record<string, any>, curr: Record<string, any>) => this.mergeDeep(acc, curr),
          {}
        );
        this.translate.setTranslation(lang, merged, true);
        toLoad.forEach(m => alreadyLoaded.add(m));
        this.loaded.set(lang, alreadyLoaded);
        this.inFlight.delete(lang);
      }),
      shareReplay(1)
    );

    this.inFlight.set(lang, load$);
    return load$;
  }

  private mergeDeep(target: Record<string, any>, source: Record<string, any>): Record<string, any> {
    const output = { ...target };
    for (const key of Object.keys(source)) {
      if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key]) && target[key]) {
        output[key] = this.mergeDeep(target[key], source[key]);
      } else {
        output[key] = source[key];
      }
    }
    return output;
  }
}
